import {css, StyleSheet} from 'aphrodite';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {multiLineEllipsisText} from 'styles/Utilities';
import {
  GRAY_DARK,
  GRAY_LIGHTER,
  WHITE,
  NEAR_BLACK,
  GRAY_DARKER,
  BLUE_LIGHTER,
  RED,
  BLUE,
  GRAY,
} from 'ui-library/Colors';
import memoizeLastCall from 'util/memoizeLastCall';

export const COMMON_NODE_WIDTH = 240;
export const NODE_NAME_BOX_HEIGHT = 48;
export const LARGE_NODE_NAME_BOX_HEIGHT = 64;
export const NODE_NAME_BOX_PADDING = 8;
export const METHOD_BEING_DRAGGED_NODE_HEIGHT = 181;
export const ACTIVE_DRAGGING_OPACITY = 0.65;
export const NOT_DRAGGING_OPACITY = 1;
export const DASHED_LINE_LENGTH = 40;
export const ADD_NODE_BUTTON_WIDTH = 20;
export const SUB_TITLE_HEIGHT = 28;
export const SUB_ELEMENT_HEIGHT = 21;
export const FLOWCHART_PADDING_X = 16;
export const FLOWCHART_PADDING_Y = 55;
export const PROCEDURE_HEADER_HEIGHT = 74;
export const MAX_PROCEDURE_FLOWCHART_DEFAULT_ZOOM = 1;
export const MIN_PROCEDURE_FLOWCHART_DEFAULT_ZOOM = 0.75;

export const BORDER_RADIUS = 5;

const styles = StyleSheet.create({
  wrapper: {
    width: `${COMMON_NODE_WIDTH}px`,
    borderRadius: BORDER_RADIUS,
  },
  nodeSelected: {
    boxShadow: `0 0 6px ${BLUE_LIGHTER}`,
  },
  node: {
    border: `1px solid ${GRAY_DARK}`,
    background: WHITE,
    width: '100%',
  },
  nodeTop: {
    borderRadius: `${BORDER_RADIUS}px ${BORDER_RADIUS}px 0px 0px`,
    borderBottom: 'none',
  },
  nodeBottom: {
    borderRadius: `0px 0px ${BORDER_RADIUS}px ${BORDER_RADIUS}px`,
    borderTop: 'none',
    cursor: 'move',
  },
  text: {
    userSelect: 'none',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    width: '100%',
  },
  nodeNameBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${NODE_NAME_BOX_PADDING}px ${NODE_NAME_BOX_PADDING}px`,
    height: `${NODE_NAME_BOX_HEIGHT}px`,
    background: GRAY_LIGHTER,
    borderTopLeftRadius: BORDER_RADIUS,
    borderTopRightRadius: BORDER_RADIUS,
    position: 'relative',
    overflow: 'visible',
    ':hover': {
      color: NEAR_BLACK,
      cursor: 'pointer',
    },
  },
  smallBox: {
    height: `${NODE_NAME_BOX_HEIGHT}px`,
  },
  largeBox: {
    height: `${LARGE_NODE_NAME_BOX_HEIGHT}px`,
  },
  nodeNameTextEllipses: {
    fontWeight: 'bold',
    fontSize: 15,
    textAlign: 'left',
    lineHeight: 1.1,
    paddingRight: '8px',
    ...multiLineEllipsisText(2),
  },
  subSection: {
    borderTop: `1px solid ${GRAY}`,
  },
  placeholderName: {
    fontStyle: 'italic',
    color: GRAY_DARKER,
  },
  activelyDraggingNode: {
    opacity: ACTIVE_DRAGGING_OPACITY,
  },
  noDraggingNode: {
    opacity: NOT_DRAGGING_OPACITY,
  },
  hasValidationErrors: {
    border: `1px solid ${RED}`,
  },
  isIntersectingDropZone: {
    border: `2px solid ${BLUE}`,
  },
});

export const makeRawOpacityStyle = (methodCidBeingDragged: string | null): object => ({
  opacity: methodCidBeingDragged != null ? ACTIVE_DRAGGING_OPACITY : NOT_DRAGGING_OPACITY,
});
export const makeOpacityStyle = (methodCidBeingDragged: string | null): object =>
  methodCidBeingDragged != null ? styles.activelyDraggingNode : styles.noDraggingNode;
export const makeOpacityClass = (methodCidBeingDragged: string | null): string =>
  css(makeOpacityStyle(methodCidBeingDragged));

export const nodeWrapperStyle = styles.wrapper;
export const makeNodeWrapperWithOpacityClass = (
  isMethodSelected: boolean,
  methodCidBeingDragged: string | null
): string =>
  css(nodeWrapperStyle, isMethodSelected && styles.nodeSelected, makeOpacityStyle(methodCidBeingDragged));

export const nodeNameBoxClass = (
  hasValidationErrors: boolean,
  hasDraftNameErrors: boolean = false,
  isIntersectingDropZone: boolean = false
): string =>
  css(
    styles.node,
    isIntersectingDropZone && styles.isIntersectingDropZone,
    hasValidationErrors && styles.hasValidationErrors,
    hasDraftNameErrors ? styles.smallBox : styles.largeBox,
    styles.nodeTop,
    styles.text,
    styles.nodeNameBox
  );
export const nodeNameTextEllipsesClass = css(styles.text, styles.nodeNameTextEllipses);
export const methodBodyClass = (
  hasValidationErrors: boolean,
  isIntersectingDropZone: boolean = false
): string =>
  css(
    styles.node,
    isIntersectingDropZone && styles.isIntersectingDropZone,
    hasValidationErrors && styles.hasValidationErrors,
    styles.nodeBottom
  );
export const subSectionClass = css(styles.subSection);
export const textClass = css(styles.text);

export const makePlaceholderNameClass = (methodName: string): string =>
  css(!methodName && styles.placeholderName);

export const getMethodNamePlaceholder = memoizeLastCall(
  (procedureType: ProcedureType): string =>
    `Untitled ${getProcedureUIString(ProceduresStringKeys.METHOD, procedureType, {
      stringCase: StringCase.LOWER,
    })}`
);

export const getMethodNameOrPlaceholder = (methodName: string, procedureType: ProcedureType): string =>
  methodName || getMethodNamePlaceholder(procedureType);

// Adding the zIndex here is required to ensure that as we draw the material edge connection line,
// The connection line is visible above the method.
// Ideally it would be layered below the material handle, but the stacking contexts don't work out
// that way given how ReactFlow renders nodes and edges, so for now the connection line is above handles.
// (Note that the number 1001 was given to us by the ReactFlow team.)
export const nodeZIndex = 1001;
export const nodeZIndexStyle = {zIndex: nodeZIndex};
